/**
 * Battle boosts (owner 2026-10-21: "1 hour power ups … for damage, health and other stats, for gems"), parsed from
 * shop.json `boosts`: each raises one combat stat by a percent for every battle started while it lasts (real time, from
 * the purchase). The rules are meta/boosts.ts; the sim never reads this data: a boost reaches a battle only through the
 * run's GearBonus list, fixed when the battle starts (and kept by its suspend record), like VIP's combat bonus.
 */

/** The stats a boost can raise, in the Shop's order. */
export const BOOST_STATS = ["damage", "hp", "fireRate", "range"] as const;
export type BoostStat = (typeof BOOST_STATS)[number];

export interface BoostData {
  readonly id: string;
  readonly name: string;
  readonly stat: BoostStat;
  /** The raise, in percent of the stat (towers' damage, fire rate or range; the fortress's health). */
  readonly percent: number;
  /** How long one purchase lasts (a purchase while it runs adds this, up to `maxMinutes` ahead). */
  readonly minutes: number;
  readonly gems: number;
  /** The painted icon (art/interface/icons/<icon>.png). */
  readonly icon: string;
}

export interface BoostsData {
  readonly items: readonly BoostData[];
  /** A boost never runs more than this far ahead (stacked purchases). */
  readonly maxMinutes: number;
  /** The Shop sells boosts once this level is cleared. */
  readonly unlockAfterLevel: number;
}

export const NO_BOOSTS: BoostsData = { items: [], maxMinutes: 0, unlockAfterLevel: Number.MAX_SAFE_INTEGER };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json, where: string, min = 0): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= min) return v;
  throw new TypeError(`${where}: expected an integer >= ${min}, got ${JSON.stringify(v)}`);
}
function str(v: Json, where: string): string {
  if (typeof v === "string" && v.length > 0) return v;
  throw new TypeError(`${where}: expected a string`);
}

/** shop.json `boosts`; absent: none. Ids unique, one boost per stat. */
export function parseBoosts(b: Json): BoostsData {
  if (b === undefined || b === null) return NO_BOOSTS;
  if (!Array.isArray(b.items)) throw new Error("shop.boosts.items: a list");
  const seen = new Set<string>();
  const items = (b.items as Json[]).map((x, i): BoostData => {
    const w = `shop.boosts.items[${i}]`;
    const stat = x?.stat;
    if (!(BOOST_STATS as readonly string[]).includes(stat)) throw new Error(`${w}: unknown stat '${String(stat)}'`);
    if (seen.has(stat)) throw new Error(`${w}: one boost per stat`);
    seen.add(stat);
    return { id: str(x.id, `${w}.id`), name: str(x.name, `${w}.name`), stat, percent: int(x.percent, `${w}.percent`, 1), minutes: int(x.minutes, `${w}.minutes`, 1), gems: int(x.gems, `${w}.gems`, 1), icon: str(x.icon, `${w}.icon`) };
  });
  const maxMinutes = int(b.maxMinutes, "shop.boosts.maxMinutes", 1);
  for (const it of items) if (it.minutes > maxMinutes) throw new Error(`shop.boosts: ${it.id} lasts longer than maxMinutes`);
  return { items, maxMinutes, unlockAfterLevel: int(b.unlockAfterLevel ?? 0, "shop.boosts.unlockAfterLevel") };
}
